import { Application } from '@/types/application';
import { BackendNode } from '@/types/backendNode';
import { EdgeTheme } from '@/types/theme';
import { findSubroutine } from '@/util/application';
import { isConnection, isXYPosition } from '@/util/typeGuards';
import { Edge, HandleType, MarkerType, Node, Position, XYPosition } from '@xyflow/react';

export type CustomNode = Node & {
  data: {
    handles: CustomHandle[];
  };
};

export type CustomHandle = {
  id: string;
  type: HandleType;
  label?: string;
};

const extractHandlesFromEdges = (edges: Edge[]): Record<string, CustomHandle[]> => {
  const nodeHandles: Record<string, CustomHandle[]> = {};

  edges.forEach((edge) => {
    const { source, target, sourceHandle, targetHandle, data } = edge;
    if (sourceHandle) {
      if (!nodeHandles[source]) {
        nodeHandles[source] = [];
      }
      nodeHandles[source].push({
        id: sourceHandle,
        type: 'source',
        label: data?.label?.toString() || ''
      });
    }
    if (targetHandle) {
      if (!nodeHandles[target]) {
        nodeHandles[target] = [];
      }
      nodeHandles[target].push({
        id: targetHandle,
        type: 'target',
        label: data?.label?.toString() || ''
      });
    }
  });

  return nodeHandles;
};

const populateNodesWithHandles = (nodes: Node[], nodeHandles: Record<string, CustomHandle[]>): CustomNode[] => {
  return nodes.map((node) => {
    const handles = nodeHandles[node.id] || [];
    return {
      ...node,
      data: {
        ...node.data,
        handles: handles
      }
    };
  });
};

export const getTargetId = (id: string | number | null) => `${id}.target`;

const blockAdapter = () => {
  const toFlow = (
    application: Application,
    selectedSubroutineId: number,
    selectedNodeIds: string[],
    edgeTheme: EdgeTheme,
    callback: (nodes: Node[], edges: Edge[]) => void
  ) => {
    const subroutine = findSubroutine(application.subroutines, selectedSubroutineId);
    if (!subroutine) return;

    const nodeIds: number[] = subroutine.nodes || application.subroutines[0].nodes;
    const filteredNodes = Object.keys(application.blocks.nodes)
      .filter((key) => nodeIds.includes(Number(key)))
      .reduce((obj: Record<string, BackendNode>, key: string) => {
        obj[key] = application.blocks.nodes[key];
        return obj;
      }, {});

    const nodes: Node[] = Object.entries(filteredNodes).map(([backendNodeId, backendNode], index) => {
      const details = application.blocks.details[backendNodeId];
      const data = {
        id: backendNodeId,
        data: {
          backendNode: backendNode,
          ...(details && { details: details })
        }
      };
      const savedPosition = application.blocks.layouts[backendNodeId]?.position;
      const pos: XYPosition = isXYPosition(savedPosition) ? savedPosition : { x: index * 200, y: index * 0 };

      return {
        id: backendNodeId,
        position: pos,
        data: data.data,
        type: backendNode.type === 'Note' ? 'customNote' : 'customNode',
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        selected: selectedNodeIds.includes(backendNodeId),
        ...(backendNode.type === 'Note' && {
          dragHandle: '.custom-drag-handle'
        })
      };
    });
    const edges: Edge[] = Object.entries(application.blocks.connections)
      .map(([ConnectionSourceId, actionConnectionRecord], index) => {
        return actionConnectionRecord
          .filter((connection) => isConnection(connection))
          .map((connection) => {
            const edge: Edge = {
              id: `${ConnectionSourceId}.source.${connection.id}.${index}`,
              source: ConnectionSourceId,
              target: connection.dst?.toString() || '',
              sourceHandle: `${ConnectionSourceId}.source.${connection.id}`,
              targetHandle: getTargetId(connection.dst),
              type: edgeTheme,
              reconnectable: 'target',
              markerEnd: {
                type: MarkerType.ArrowClosed,
                markerUnits: 'userSpaceOnUse',
                height: 30,
                width: 30
              },
              data: {
                action: connection.action,
                id: connection.id,
                dst: connection.dst,
                label: connection.label || connection.action
              }
            };
            return edge;
          });
      })
      .flat();

    const nodeHandles = extractHandlesFromEdges(edges);
    const updatedNodes = populateNodesWithHandles(nodes, nodeHandles);
    callback(updatedNodes, edges);
  };

  return {
    toFlow
  };
};

export default blockAdapter;
